import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppScope } from "./AppCover";
import Icon from "./Icon";
import { STATE_LAYER } from "./IconButton";

const EDGE = 8;

/*
  Figma "Menu": bg/raised, a 1px separator edge, radius/lg and 8 padding, opening under
  `anchorRef` from 600px up and rising as the Sheet on phones. It is portaled, because a
  page's panel is transformed and clips, so the theme and app colour are carried over from
  the anchor. Picking anything inside closes it; a control that keeps it open stops the click.
  `align` "end" lines the right edges up, "start" the left ones. With `autoFocus` the chosen
  item (or the first) takes focus on opening; the arrow keys move between items either way.
*/
const ITEMS = '[role="menuitem"]:not(:disabled), [role="option"]:not(:disabled)';

const Menu = ({ open, onClose, anchorRef, width = 240, align = "end", label, autoFocus = false, children }) => {
  const [box, setBox] = useState(null);
  const panelRef = useRef(null);
  const app = useAppScope();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      return undefined;
    }
    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const height = panelRef.current?.offsetHeight || 0;
      const fits = rect.bottom + EDGE + height <= window.innerHeight - EDGE;
      const left = align === "end" ? rect.right - width : rect.left;
      setBox({
        top: fits ? rect.bottom + EDGE : Math.max(EDGE, rect.top - EDGE - height),
        left: Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE)),
      });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, anchorRef, width, align]);

  // Waits for the popover's position: it stays invisible, and so unfocusable, until then.
  const placed = Boolean(box);
  useEffect(() => {
    if (!open || !autoFocus || (!placed && window.innerWidth >= 600)) return;
    const panel = panelRef.current;
    (panel?.querySelector('[aria-selected="true"]:not(:disabled)') || panel?.querySelector(ITEMS))?.focus({ preventScroll: true });
    panel?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, autoFocus, placed]);

  useEffect(() => {
    if (!open) return undefined;
    // Captured first and stopped, so Esc closes only the menu, not a popup it sits in.
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      closeRef.current?.();
      const anchor = anchorRef.current;
      (anchor?.matches?.("button, a") ? anchor : anchor?.querySelector?.("button, a"))?.focus?.();
    };
    const onPress = (event) => {
      if (panelRef.current?.contains(event.target) || anchorRef.current?.contains(event.target)) return;
      closeRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPress);
    document.addEventListener("touchstart", onPress);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPress);
      document.removeEventListener("touchstart", onPress);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  const theme = anchorRef.current?.closest("[data-theme]")?.getAttribute("data-theme") || undefined;
  const pick = (event) => {
    if (event.target.closest("button, a")) onClose();
  };
  const moveFocus = (event) => {
    const keys = { ArrowDown: 1, ArrowUp: -1, Home: "first", End: "last" };
    if (!(event.key in keys)) return;
    const items = [...panelRef.current.querySelectorAll(ITEMS)];
    if (!items.length) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement);
    const step = keys[event.key];
    const next =
      step === "first" ? 0 : step === "last" ? items.length - 1 : (at + step + items.length) % items.length;
    items[next].focus();
  };

  return createPortal(
    window.innerWidth < 600 ? (
      <div data-theme={theme} data-app={app || undefined} className="fixed inset-0 z-[600] font-ds text-label-primary">
        <div className="absolute inset-0 bg-overlay-scrim animate-ds-fade-in" onClick={onClose} aria-hidden="true" />
        <div
          ref={panelRef}
          role="menu"
          aria-label={label}
          onClick={pick}
          onKeyDown={moveFocus}
          className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto overscroll-contain rounded-t-radius-xl bg-bg-raised px-space-12 pb-space-32 pt-space-8 shadow-overlay animate-ds-sheet-in"
        >
          <div className="flex justify-center py-space-4" aria-hidden="true">
            <span className="h-[5px] w-[36px] rounded-full bg-fill-secondary" />
          </div>
          {children}
        </div>
      </div>
    ) : (
      <div
        ref={panelRef}
        role="menu"
        aria-label={label}
        data-theme={theme}
        data-app={app || undefined}
        onClick={pick}
        onKeyDown={moveFocus}
        style={box ? { top: box.top, left: box.left, width } : { width, visibility: "hidden" }}
        className="fixed z-[600] max-h-[calc(100vh-16px)] overflow-y-auto overscroll-contain rounded-radius-lg border border-separator-default bg-bg-raised p-space-8 font-ds text-label-primary shadow-overlay animate-ds-pop-in"
      >
        {children}
      </div>
    ),
    document.body
  );
};

/*
  Figma "Menu item": 44 tall, a 20 icon (or `leading`, for an avatar) and a Body label.
  `selected` is the quaternary fill, `danger` the Destructive red, `detail` the row's end.
*/
export const MenuItem = ({
  as: Tag = "button",
  icon,
  leading,
  detail,
  selected = false,
  danger = false,
  children,
  className = "",
  ...rest
}) => (
  <Tag
    {...(Tag === "button" ? { type: "button" } : {})}
    role="menuitem"
    className={`relative flex min-h-[44px] w-full items-center gap-space-12 rounded-radius-sm px-space-12 py-space-8 text-left text-body outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-focus-ring)] disabled:cursor-not-allowed disabled:opacity-40 ${
      danger ? "text-status-danger" : "text-label-primary"
    } ${selected ? "bg-fill-quaternary" : ""} ${STATE_LAYER} ${className}`}
    {...rest}
  >
    {leading}
    {icon && <Icon name={icon} size={20} className="shrink-0" />}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    {detail}
  </Tag>
);

/** Figma's separator between menu sections: a hairline with 8 either side. */
export const MenuSeparator = () => (
  <div role="separator" className="py-space-8">
    <div className="h-px bg-separator-default" />
  </div>
);

export default Menu;
